/**
 * Setcard-Panel für die PB-Sidebar.
 *
 * Bündelt alle Setcard-Aktionen in einer Card:
 *   • Setcard ansehen — öffnet den öffentlichen Setcard-Link direkt (neuer Tab;
 *     ohne bestehenden Link wird zuerst einer erzeugt)
 *   • Setcard (PDF) — PDF-Download
 *   • Link/PIN verwalten (erzeugen, kopieren, neu erzeugen, widerrufen)
 *
 * Standardmäßig eingeklappt (nur Titelzeile), damit die Sidebar kompakt bleibt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../components/ui';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import {
  getPbSetcardLink,
  createPbSetcardLink,
  deletePbSetcardLink,
  downloadPbSetcard,
  type PbSetcardLinkInfo,
} from '../../../lib/api';
import { formatDateTime } from '../../../lib/dateFormat';

/** Baut die öffentliche Setcard-URL aus dem Token. */
function setcardLinkUrl(token: string): string {
  return `${window.location.origin}/setcard/${token}`;
}

function fmtDateTime(iso: string | null): string {
  return formatDateTime(iso, '');
}

interface Props {
  userId: number;
  dark: boolean;
}

export function SetcardSharePanel({ userId, dark }: Props) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [link, setLink] = useState<PbSetcardLinkInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<'url' | 'pin' | null>(null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getPbSetcardLink(userId)
      .then(info => { if (alive) setLink(info); })
      .catch(() => { if (alive) setError(tr('pflege.schedule.link_panel.load_error')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId, tr]);

  const run = useCallback(async (fn: () => Promise<PbSetcardLinkInfo | null>) => {
    setBusy(true);
    setError(null);
    try {
      setLink(await fn());
    } catch {
      setError(tr('pflege.schedule.link_panel.action_error'));
    } finally {
      setBusy(false);
      setConfirmRegen(false);
    }
  }, [tr]);

  const handleCreate = () => run(() => createPbSetcardLink(userId));
  const handleRegenerate = () => run(() => createPbSetcardLink(userId));
  const handleRevoke = () => run(async () => { await deletePbSetcardLink(userId); return null; });

  // ── Setcard ansehen — öffnet den Setcard-Link direkt in einem neuen Tab.
  // Ohne bestehenden Link wird zuerst einer erzeugt. Das Fenster wird
  // synchron geöffnet (Popup-Blocker) und bekommt danach die Ziel-URL.
  const [viewBusy, setViewBusy] = useState(false);
  const handleView = async () => {
    if (viewBusy) return;
    const win = window.open('', '_blank');
    setViewBusy(true);
    setError(null);
    try {
      let current = link;
      if (!current) {
        current = await createPbSetcardLink(userId);
        setLink(current);
      }
      const url = setcardLinkUrl(current.token);
      if (win) win.location.href = url; else window.open(url, '_blank');
    } catch {
      if (win) win.close();
      setError(tr('pflege.detail.setcard_panel.open_error'));
    } finally {
      setViewBusy(false);
    }
  };

  // ── Setcard-PDF-Download ──
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const handlePdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      await downloadPbSetcard(userId);
    } catch {
      setPdfError(tr('pflege.detail.setcard_error'));
    } finally {
      setPdfBusy(false);
    }
  };

  const copy = async (kind: 'url' | 'pin', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      setTimeout(() => setCopied(c => (c === kind ? null : c)), 1500);
    } catch { /* clipboard nicht verfügbar */ }
  };

  const readonlyField = (label: string, value: string, kind: 'url' | 'pin') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: tc.muted }}>{label}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          readOnly
          value={value}
          onFocus={e => e.currentTarget.select()}
          style={{
            flex: 1, fontSize: kind === 'pin' ? 16 : 12, letterSpacing: kind === 'pin' ? '0.3em' : undefined,
            padding: '7px 9px', border: `1px solid ${tc.border}`, borderRadius: 8, background: tc.cardMuted,
            color: tc.text, fontFamily: kind === 'pin' ? 'monospace' : 'Outfit,sans-serif', outline: 'none',
            fontWeight: kind === 'pin' ? 700 : 400, minWidth: 0,
          }}
        />
        <Button dark={dark} variant="secondary" size="sm" onClick={() => copy(kind, value)}>
          {copied === kind ? '✓' : tr('common.action.copy')}
        </Button>
      </div>
    </div>
  );

  return (
    <Card dark={dark} style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontFamily: 'Outfit,sans-serif', textAlign: 'left',
        }}
      >
        <h3 style={{ margin: 0, color: tc.heading, fontSize: 14, fontWeight: 700 }}>{tr('pflege.detail.setcard_panel.title')}</h3>
        <span aria-hidden style={{
          color: tc.muted, fontSize: 14, lineHeight: 1,
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s',
        }}>›</span>
      </button>

      {open && (<>

      {/* Setcard ansehen (öffnet Link direkt) + PDF-Download */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <button
          type="button"
          onClick={handleView}
          disabled={viewBusy || loading}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            width: '100%', background: BRAND_LIGHT, color: BRAND, border: `1px solid ${BRAND}`,
            borderRadius: 8, padding: '8px 13px',
            cursor: viewBusy ? 'wait' : 'pointer', opacity: viewBusy ? 0.7 : 1,
            fontSize: 13, fontWeight: 600, fontFamily: 'Outfit,sans-serif',
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>👁</span>
          {viewBusy ? tr('pflege.detail.setcard_view_loading') : tr('pflege.detail.setcard_view')}
        </button>
        <button
          type="button"
          onClick={handlePdf}
          disabled={pdfBusy}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            width: '100%', background: 'transparent', color: BRAND, border: `1px solid ${BRAND}`,
            borderRadius: 8, padding: '8px 13px',
            cursor: pdfBusy ? 'wait' : 'pointer', opacity: pdfBusy ? 0.7 : 1,
            fontSize: 13, fontWeight: 600, fontFamily: 'Outfit,sans-serif',
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>⬇</span>
          {pdfBusy ? tr('pflege.detail.setcard_loading') : tr('pflege.detail.setcard_download')}
        </button>
        {pdfError && <div style={{ fontSize: 11.5, color: '#b42318' }}>{pdfError}</div>}
      </div>

      <p style={{ margin: 0, color: tc.muted, fontSize: 12, lineHeight: 1.5 }}>
        {tr('pflege.detail.setcard_panel.description')}
      </p>

      {error && (
        <div role="alert" style={{ padding: '7px 9px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 12, color: '#b42318' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: tc.muted }}>{tr('pflege.detail.setcard_view_loading')}</div>
      ) : !link ? (
        <div>
          <Button dark={dark} variant="primary" size="sm" disabled={busy} onClick={handleCreate}>
            {busy ? tr('pflege.schedule.link_panel.creating') : tr('pflege.detail.setcard_panel.create_button')}
          </Button>
        </div>
      ) : (
        <>
          {readonlyField(tr('pflege.schedule.link_panel.link_label'), setcardLinkUrl(link.token), 'url')}
          {readonlyField(tr('pflege.schedule.link_panel.pin_label'), link.pin, 'pin')}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {!confirmRegen ? (
              <Button dark={dark} variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmRegen(true)}>
                {tr('pflege.schedule.link_panel.regenerate_button')}
              </Button>
            ) : (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12, color: tc.text }}>
                {tr('pflege.schedule.link_panel.regenerate_confirm')}
                <Button dark={dark} variant="primary" size="sm" disabled={busy} onClick={handleRegenerate}>{tr('pflege.schedule.link_panel.regenerate_confirm_yes')}</Button>
                <Button dark={dark} variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmRegen(false)}>{tr('common.action.cancel')}</Button>
              </span>
            )}

            <button
              type="button"
              onClick={handleRevoke}
              disabled={busy}
              style={{ fontSize: 12, padding: '6px 10px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fecdca', borderRadius: 7, color: '#b42318', cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'Outfit,sans-serif' }}
            >
              {tr('pflege.schedule.link_panel.revoke_button')}
            </button>
          </div>

          {link.last_accessed_at && (
            <div style={{ fontSize: 11, color: tc.muted }}>{tr('pflege.schedule.link_panel.last_accessed', { date: fmtDateTime(link.last_accessed_at) })}</div>
          )}
        </>
      )}
      </>)}
    </Card>
  );
}
